import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FileUploadZone = ({ 
  onFilesImported, 
  isProcessing, 
  processingStatus,
  acceptedFormats = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'application/pdf']
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Detect mobile devices (iPhone, iPad, Android)
  const isMobile = /iPhone|iPad|iPod|Android/i?.test(navigator?.userAgent);
  const isIOS = /iPhone|iPad|iPod/i?.test(navigator?.userAgent);

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleDragIn = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragCounter(dragCounter + 1);
    if (e?.dataTransfer?.items && e?.dataTransfer?.items?.length > 0) {
      setIsDragActive(true);
    }
  };

  const handleDragOut = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const newCounter = dragCounter - 1;
    setDragCounter(newCounter);
    if (newCounter === 0) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragActive(false);
    setDragCounter(0);
    setUploadError('');

    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.length > 0) {
      const files = Array.from(e?.dataTransfer?.files);
      handleFiles(files);
    }
  };

  const handleFileInput = (e) => {
    setUploadError('');
    const files = Array.from(e?.target?.files || []);
    
    if (files?.length === 0) {
      if (isIOS) {
        setUploadError('No files selected. On iPhone, make sure to tap "Choose Files" and select from Photo Library or Files app.');
      }
      return;
    }
    
    handleFiles(files);
    // Reset input value to allow selecting same file again
    if (e?.target) {
      e.target.value = '';
    }
  };

  const handleFiles = (files) => {
    if (isProcessing || !files?.length) return;

    // Filter for accepted file types with better iPhone compatibility
    const validFiles = files?.filter(file => {
      // More flexible file type checking for iPhone
      const fileName = file?.name?.toLowerCase() || '';
      const fileType = file?.type?.toLowerCase() || '';
      
      // Check by file extension (more reliable on iPhone)
      if (fileName?.endsWith('.png') || fileName?.endsWith('.jpg') || fileName?.endsWith('.jpeg')) {
        return true;
      }
      if (fileName?.endsWith('.svg')) {
        return acceptedFormats?.includes('image/svg+xml');
      }
      if (fileName?.endsWith('.pdf')) {
        return acceptedFormats?.includes('application/pdf');
      }
      
      // Check by MIME type as fallback
      return acceptedFormats?.some(format => {
        if (format === 'image/svg+xml') {
          return fileType === 'image/svg+xml' || fileType === 'image/svg' || fileName?.endsWith('.svg');
        }
        if (format === 'application/pdf') {
          return fileType === 'application/pdf' || fileName?.endsWith('.pdf');
        }
        if (format === 'image/jpeg') {
          return fileType === 'image/jpeg' || fileType === 'image/jpg' || fileName?.endsWith('.jpg') || fileName?.endsWith('.jpeg');
        }
        if (format === 'image/png') {
          return fileType === 'image/png' || fileName?.endsWith('.png');
        }
        return fileType === format;
      });
    });

    if (validFiles?.length === 0) {
      const errorMsg = isIOS 
        ? 'Please select valid image files (PNG, JPEG, SVG) or PDF documents. Make sure the files are saved to your Photos or Files app.'
        : 'Please upload valid image files (PNG, JPEG, SVG) or PDF documents.';
      setUploadError(errorMsg);
      return;
    }

    if (validFiles?.length !== files?.length) {
      const invalidCount = files?.length - validFiles?.length;
      const warningMsg = isIOS
        ? `${invalidCount} file(s) were skipped. On iPhone, only PNG, JPEG, SVG, and PDF files from Photos or Files app are supported.`
        : `${invalidCount} file(s) were skipped due to unsupported format. Only PNG, JPEG, SVG, and PDF files are accepted.`;
      setUploadError(warningMsg);
    }

    if (validFiles?.length > 0) {
      setUploadError('');
      onFilesImported(validFiles);
    }
  };

  const formatFileTypes = () => {
    const types = [];
    if (acceptedFormats?.includes('image/png') || acceptedFormats?.includes('image/jpeg')) {
      types?.push('PNG, JPEG');
    }
    if (acceptedFormats?.includes('image/svg+xml')) {
      types?.push('SVG');
    }
    if (acceptedFormats?.includes('application/pdf')) {
      types?.push('PDF');
    }
    return types?.join(', ');
  };

  // Handle photo library access specifically for iPhone
  const handlePhotoLibraryAccess = (e) => {
    e?.stopPropagation();
    if (fileInputRef?.current) {
      fileInputRef?.current?.click();
    }
  };

  // Handle camera access specifically for iPhone
  const handleCameraAccess = (e) => {
    e?.stopPropagation();
    if (cameraInputRef?.current) {
      cameraInputRef?.current?.click();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Upload QR Code Files</h3>
        <div className="text-sm text-gray-500">
          Supports: {formatFileTypes()}
        </div>
      </div>
      {/* iPhone-specific instructions */}
      {isIOS && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <h4 className="font-semibold mb-2">iPhone Upload Options:</h4>
              <div className="space-y-2">
                <p>• <strong>Existing Photos:</strong> Tap "Choose from Photos" to select QR images from your Photo Library</p>
                <p>• <strong>Take New Photo:</strong> Tap "Take Photo" to capture a QR code with your camera</p>
                <p>• <strong>Files App:</strong> Use "Choose from Files" to select from Documents or other apps</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Error message display */}
      {uploadError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <div className="flex items-start space-x-2">
            <Icon name="AlertCircle" size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{uploadError}</p>
          </div>
        </div>
      )}
      {/* Main Upload Zone */}
      <div
        className={`
          relative border-2 border-dashed rounded-xl p-6 text-center transition-all duration-300
          ${isDragActive 
            ? 'border-blue-400 bg-blue-50 border-solid' :'border-gray-300 bg-gray-50'
          }
          ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}
          ${isMobile ? '' : 'hover:border-gray-400 hover:bg-gray-100 cursor-pointer'}
        `}
        onDrag={!isMobile ? handleDrag : undefined}
        onDragStart={!isMobile ? handleDrag : undefined}
        onDragEnd={!isMobile ? handleDrag : undefined}
        onDragOver={!isMobile ? handleDrag : undefined}
        onDragEnter={!isMobile ? handleDragIn : undefined}
        onDragLeave={!isMobile ? handleDragOut : undefined}
        onDrop={!isMobile ? handleDrop : undefined}
        onClick={!isMobile && !isProcessing ? () => fileInputRef?.current?.click() : undefined}
      >
        {/* Hidden file inputs with proper iPhone configuration */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedFormats?.join(',')}
          onChange={handleFileInput}
          className="absolute inset-0 w-full h-full opacity-0"
          style={{ pointerEvents: 'none' }}
          disabled={isProcessing}
        />
        
        {/* Separate camera input for iPhone */}
        {isIOS && (
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileInput}
            className="absolute inset-0 w-full h-full opacity-0"
            style={{ pointerEvents: 'none' }}
            disabled={isProcessing}
          />
        )}

        <div className="space-y-4">
          {isProcessing ? (
            <>
              <div className="w-16 h-16 mx-auto">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
              </div>
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">Processing Files...</h4>
                <p className="text-sm text-gray-600">{processingStatus}</p>
              </div>
            </>
          ) : (
            <>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto transition-colors duration-300 ${
                isDragActive ? 'bg-blue-200' : 'bg-blue-100'
              }`}>
                <Icon 
                  name={isDragActive ? "Download" : (isMobile ? "Image" : "Upload")} 
                  size={24} 
                  className={`${isDragActive ? 'text-blue-700' : 'text-blue-600'} transition-colors duration-300`} 
                />
              </div>
              
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                  {isDragActive 
                    ? 'Drop files here to upload' 
                    : (isIOS 
                      ? 'Select QR Code Images' :'Drag & drop QR code files or click to browse'
                    )
                  }
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  {isIOS 
                    ? 'Choose from existing photos or take a new photo' :'Upload images or PDF files containing QR codes'
                  }
                </p>

                {/* iPhone-specific buttons */}
                {isIOS ? (
                  <div className="flex flex-col space-y-3 max-w-xs mx-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePhotoLibraryAccess}
                      disabled={isProcessing}
                      className="w-full flex items-center justify-center space-x-2 bg-white hover:bg-gray-50"
                    >
                      <Icon name="Image" size={16} />
                      <span>Choose from Photos</span>
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCameraAccess}
                      disabled={isProcessing}
                      className="w-full flex items-center justify-center space-x-2 bg-white hover:bg-gray-50"
                    >
                      <Icon name="Camera" size={16} />
                      <span>Take Photo</span>
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePhotoLibraryAccess}
                      disabled={isProcessing}
                      className="w-full flex items-center justify-center space-x-2 bg-white hover:bg-gray-50"
                    >
                      <Icon name="FileText" size={16} />
                      <span>Choose from Files</span>
                    </Button>
                  </div>
                ) : (
                  // Desktop/Android button
                  (<Button
                    variant="outline"
                    size="sm"
                    className="mx-auto"
                    disabled={isProcessing}
                    onClick={(e) => {
                      e?.stopPropagation();
                      fileInputRef?.current?.click();
                    }}
                  >
                    <Icon name={isMobile ? "FolderOpen" : "FolderOpen"} size={16} className="mr-2" />
                    {isMobile ? "Choose Files" : "Browse Files"}
                  </Button>)
                )}
              </div>
            </>
          )}
        </div>

        {/* Format indicators */}
        {!isProcessing && (
          <div className="mt-6 flex justify-center items-center space-x-4 text-xs text-gray-500">
            <div className="flex items-center space-x-1">
              <Icon name="Image" size={12} />
              <span>Images</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="FileImage" size={12} />
              <span>Vectors</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="FileText" size={12} />
              <span>PDFs</span>
            </div>
          </div>
        )}

        {/* Drag overlay for desktop */}
        {isDragActive && !isMobile && (
          <div className="absolute inset-0 bg-blue-100 bg-opacity-80 border-2 border-blue-400 border-dashed rounded-xl flex items-center justify-center">
            <div className="text-center">
              <Icon name="Download" size={32} className="text-blue-600 mx-auto mb-2" />
              <p className="text-blue-700 font-medium">Drop your files here</p>
            </div>
          </div>
        )}
      </div>
      {/* Upload Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-800 mb-2">Import Tips:</h4>
        <ul className="text-xs text-blue-700 space-y-1">
          <li>• Multiple QR codes can be processed simultaneously</li>
          <li>• High-resolution images provide better decoding accuracy</li>
          <li>• PDF files will be scanned for embedded QR codes</li>
          <li>• Original styling and colors will be automatically detected</li>
          {isIOS ? (
            <>
              <li>• For best results, ensure QR codes are clearly visible and well-lit</li>
              <li>• If you can't see your photos, check iPhone Settings &gt; Privacy &gt; Camera/Photos permissions</li>
              <li>• Use Files app to access QR images from cloud storage or other apps</li>
            </>
          ) : isMobile ? (
            <>
              <li>• Use good lighting when taking photos of QR codes</li>
              <li>• Hold your device steady and close enough to fill the frame</li>
            </>
          ) : (
            <li>• Drag and drop multiple files at once for batch processing</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default FileUploadZone;